import { useCallback, useEffect, useRef, useState } from 'react';
import { rpcClient } from 'src/api/rpc/client';
import type { KernelTelemetryEnvelope, KernelTelemetrySummary } from '@erp/rpc';
import {
  kernelTelemetryMatchesCategory,
  mergeKernelTelemetryEvents,
} from 'src/components/common/kernelTelemetryDisplay';

export function useKernelTelemetryEvents({
  agentRunId,
  enabled,
  live,
  category,
}: {
  agentRunId: string | null;
  enabled: boolean;
  live: boolean;
  category: string | undefined;
}) {
  const [events, setEvents] = useState<KernelTelemetryEnvelope[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [summary, setSummary] = useState<KernelTelemetrySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastSeqRef = useRef<number | undefined>(undefined);
  const categoryRef = useRef(category);
  categoryRef.current = category;

  useEffect(() => {
    lastSeqRef.current = events.reduce<number | undefined>((max, envelope) => {
      return max === undefined || envelope.seq > max ? envelope.seq : max;
    }, undefined);
  }, [events]);

  useEffect(() => {
    if (!enabled || !agentRunId) return;
    let cancelled = false;
    setLoading(true);
    setEvents([]);
    setNextCursor(null);
    setSummary(null);
    void rpcClient.agentDefinitions
      .getKernelTelemetry({
        agentRunId,
        limit: 100,
        category: category ? [category] : undefined,
      })
      .then((page) => {
        if (cancelled) return;
        setEvents((current) =>
          mergeKernelTelemetryEvents(current, page.events),
        );
        setNextCursor(page.nextCursor);
        setSummary(page.summary);
      })
      .catch(() => {
        if (cancelled) return;
        setEvents([]);
        setNextCursor(null);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentRunId, enabled, category]);

  useEffect(() => {
    if (!enabled || !live || !agentRunId) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const iterator =
          await rpcClient.agentDefinitions.subscribeKernelTelemetry(
            { agentRunId, lastSeq: lastSeqRef.current },
            { signal: controller.signal },
          );
        for await (const envelope of iterator) {
          if (controller.signal.aborted) return;
          setEvents((current) => {
            if (
              !kernelTelemetryMatchesCategory(envelope, categoryRef.current)
            ) {
              return current;
            }
            return mergeKernelTelemetryEvents(current, [envelope]);
          });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.warn('[useKernelTelemetryEvents] live stream failed', error);
      }
    })();
    return () => controller.abort();
  }, [agentRunId, enabled, live]);

  const loadMore = useCallback(async () => {
    if (!agentRunId || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await rpcClient.agentDefinitions.getKernelTelemetry({
        agentRunId,
        cursor: nextCursor,
        limit: 100,
        category: category ? [category] : undefined,
      });
      setEvents((current) => mergeKernelTelemetryEvents(current, page.events));
      setNextCursor(page.nextCursor);
      if (page.summary) setSummary(page.summary);
    } finally {
      setLoadingMore(false);
    }
  }, [agentRunId, nextCursor, loadingMore, category]);

  return { events, nextCursor, summary, loading, loadingMore, loadMore };
}
